import { Theme, ThemeUICSSObject } from 'theme-ui';
import merge from 'lodash-es/merge';
import { EventEmitter, setValueByPath, useMount } from '@apia/util';
import { useRef, useState } from 'react';
import { getMainTheme } from './getMainTheme';
import { CustomTheme } from '../themeuiExports';
import { TCustomPalette, TThemeModifier } from '../base/colors/types';

let declaredStyles: ThemeUICSSObject = {};

export function getInjectedStyles() {
  return injectedStyles;
}

const injectedStyles: Record<string, ThemeUICSSObject> = {};

class MainThemeEmitter extends EventEmitter<{
  newStyles: {
    path: string;
    styles: ThemeUICSSObject;
  };
}> {}

export const mainThemeEmitter = new MainThemeEmitter();

function handleNewStyles(ev: { path: string; styles: ThemeUICSSObject }) {
  injectedStyles[ev.path] = ev.styles;
}

const unSubscribe = mainThemeEmitter.on('newStyles', handleNewStyles);

export function declareStyles(
  path: string,
  styles: ThemeUICSSObject | Record<string, ThemeUICSSObject>,
) {
  const newStyles = {};
  setValueByPath(newStyles, path, styles);
  declaredStyles = merge(declaredStyles, newStyles);
}

export function useMainTheme(
  customTheme?: () => CustomTheme,
  customPalette?: TCustomPalette,
  themeModifier?: TThemeModifier,
) {
  const [theme, setTheme] = useState<Theme>(
    getMainTheme(customTheme, customPalette, themeModifier),
  );

  const previousTheme = useRef<(() => CustomTheme) | undefined>(customTheme);
  const previousPalette = useRef<TCustomPalette | undefined>(customPalette);

  if (previousTheme.current !== customTheme)
    setTheme(getMainTheme(customTheme));

  previousTheme.current = customTheme;

  if (previousPalette.current !== customPalette)
    setTheme(getMainTheme(customTheme, customPalette));

  previousPalette.current = customPalette;

  useMount(() => {
    setTheme((current) => {
      unSubscribe();
      let initialTheme = { ...current };
      Object.entries(injectedStyles).forEach(([path, styles]) => {
        const newStyles = setValueByPath({}, path, styles);
        initialTheme = merge(initialTheme, newStyles);
      });
      return initialTheme;
    });
    return mainThemeEmitter.on('newStyles', (ev) => {
      const newStyles = setValueByPath({}, ev.path, ev.styles);
      setTheme((current) => merge({ ...current }, newStyles));
    });
  });

  return theme;
}
